import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Typography, Button, Space, message, Modal, Select } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";

const { Title } = Typography;

const statusMeta = (status, statusReason, t = (_k, fallback) => fallback) => {
  if (statusReason === "cancelled_by_doctor") {
    return {
      label: t("doctor_elig_status_cancelled_by_doctor", "Annulée (médecin)"),
      color: "#cf1322",
    };
  }
  if (status === "accepted") {
    return { label: t("doctor_elig_status_accepted", "Acceptée"), color: "#389e0d" };
  }
  if (status === "in_progress") {
    return {
      label: t("doctor_elig_status_pending_assigned", "En attente (kinésiologue assigné)"),
      color: "#1677ff",
    };
  }
  if (status === "rejected") {
    return { label: t("doctor_elig_status_rejected_returned", "Refusée / retournée"), color: "#cf1322" };
  }
  return { label: t("doctor_elig_status_pending_manager", "En attente (gestionnaire)"), color: "#1677ff" };
};
const containerStyle = {
  maxWidth: 1200,
  margin: "0 auto",
  padding: 24,
};

const headerCardStyle = {
  border: "1px solid #d9e8ff",
  borderRadius: 16,
  padding: 20,
  marginBottom: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  background: "#fafcff",
};

const chipsStyle = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 };

const chipStyle = (bg, color = "#1f2937") => ({
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
});

const statusTextStyle = (meta) => ({
  color: meta.color,
  fontWeight: 600,
});

export default function DoctorEligibilityList() {
  const { t } = useTranslation(["Patients", "Backend"]);
  const { token } = useToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [cancelDispatchIdLoading, setCancelDispatchIdLoading] = useState(null);
  const [reDispatchModal, setReDispatchModal] = useState({
    open: false,
    requestId: null,
    destinationClinicId: null,
    loading: false,
  });

  const fetchClinics = useCallback(async () => {
    try {
      const res = await axios.get(`${Constants.SERVER_URL}/clinics?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const options = (res.data || [])
        .filter((clinic) => clinic?.id && clinic?.name)
        .map((clinic) => ({
          value: clinic.id,
          label: `${clinic.name}${clinic?.type ? ` (${clinic.type})` : ""}`,
        }));
      setClinics(options);
    } catch (_error) {
      setClinics([]);
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${Constants.SERVER_URL}/eligibility-request?view=doctor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      message.error(t("doctor_elig_error_loading_list", "Impossible de charger la liste des requêtes."));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (!token) return;
    fetchClinics();
    fetchList();
  }, [token, fetchClinics, fetchList]);

  const summary = useMemo(() => {
    const base = { total: rows.length, pending: 0, accepted: 0, rejected: 0 };
    rows.forEach((row) => {
      if (row?.statusReason === "cancelled_by_doctor") {
        base.rejected += 1;
        return;
      }
      const status = String(row?.status || "").toLowerCase();
      if (status === "accepted") base.accepted += 1;
      else if (status === "in_progress") base.pending += 1;
      else if (status === "rejected") base.rejected += 1;
      else base.pending += 1;
    });
    return base;
  }, [rows]);

  const openReDispatchModal = (requestId) => {
    setReDispatchModal({
      open: true,
      requestId,
      destinationClinicId: null,
      loading: false,
    });
  };

  const closeReDispatchModal = () => {
    setReDispatchModal({
      open: false,
      requestId: null,
      destinationClinicId: null,
      loading: false,
    });
  };

  const confirmReDispatch = async () => {
    if (!reDispatchModal.requestId || !reDispatchModal.destinationClinicId) {
      message.warning(t("doctor_elig_warning_choose_destination_clinic", "Veuillez choisir une clinique destination."));
      return;
    }

    try {
      setReDispatchModal((prev) => ({ ...prev, loading: true }));
      await axios.post(
        `${Constants.SERVER_URL}/eligibility-request/${reDispatchModal.requestId}/dispatch`,
        { destinationClinicId: reDispatchModal.destinationClinicId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(
        t("doctor_elig_success_resent_to_new_clinic", "La demande a été renvoyée vers la nouvelle clinique.")
      );
      closeReDispatchModal();
      fetchList();
    } catch (error) {
      message.error(error?.response?.data?.message || t("doctor_elig_error_resend", "Impossible de renvoyer la demande."));
      setReDispatchModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const cancelRequest = async (record) => {
    try {
      const loadingKey = record.dispatchId || record.requestId;
      setCancelDispatchIdLoading(loadingKey);
      const endpoint = record.dispatchId
        ? `${Constants.SERVER_URL}/eligibility-request/dispatches/${record.dispatchId}/cancel`
        : `${Constants.SERVER_URL}/eligibility-request/${record.requestId}/cancel`;
      await axios.patch(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      message.success(
        t("doctor_elig_success_send_cancelled", "Envoi annulé. Vous pouvez renvoyer vers une autre clinique.")
      );
      fetchList();
    } catch (error) {
      message.error(error?.response?.data?.message || t("doctor_elig_error_cancel_send", "Impossible d'annuler cet envoi."));
    } finally {
      setCancelDispatchIdLoading(null);
    }
  };

  const confirmCancelDispatch = (record) => {
    Modal.confirm({
      title: t("doctor_elig_confirm_cancel_title", "Annuler l'envoi ?"),
      content:
        t(
          "doctor_elig_confirm_cancel_content",
          "La requête sera marquée comme annulée, puis vous pourrez la renvoyer vers une autre clinique."
        ),
      okText: t("doctor_elig_confirm_cancel_ok", "Annuler l'envoi"),
      okButtonProps: { danger: true },
      cancelText: t("button_close", { ns: "Patients", defaultValue: "Fermer" }),
      onOk: () => cancelRequest(record),
    });
  };

  const columns = [
    { title: t("doctor_elig_col_date", "Date"), dataIndex: "date" },
    {
      title: t("doctor_elig_col_patient", "Patient"),
      render: (_, record) =>
        `${record.patientFirstname || ""} ${record.patientLastname || ""}`.trim(),
    },
    {
      title: t("doctor_elig_col_contact", "Contact"),
      key: "contact",
      render: (_, record) => record.phoneNumber || record.email || "-",
    },
    { title: t("doctor_elig_col_destination_clinic", "Clinique destination"), dataIndex: "destinationClinicName" },
    { title: t("doctor_elig_col_comment", "Commentaire"), dataIndex: "comment" },
    {
      title: t("doctor_elig_col_status", "Statut"),
      key: "status",
      render: (_, record) => {
        const meta = statusMeta(record.status, record.statusReason, t);
        return <span style={statusTextStyle(meta)}>{meta.label}</span>;
      },
    },
    {
      title: t("doctor_elig_col_actions", "Actions"),
      key: "actions",
      render: (_, record) => {
        const canCancel = Boolean(record.canCancel && record.requestId);
        const canResend = Boolean(record.canResend && record.requestId);
        if (!canCancel && !canResend) return null;

        return (
          <Space>
            {canCancel && (
              <Button
                danger
                loading={cancelDispatchIdLoading === (record.dispatchId || record.requestId)}
                onClick={() => confirmCancelDispatch(record)}
              >
                {t("doctor_elig_action_cancel_send", "Annuler l'envoi")}
              </Button>
            )}
            {canResend && (
              <Button type="primary" onClick={() => openReDispatchModal(record.requestId)}>
                {t("doctor_elig_action_resend_to_other_clinic", "Renvoyer à une autre clinique")}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={containerStyle}>
      <div style={headerCardStyle}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            {t("doctor_elig_title", "Mes requêtes d'éligibilité")}
          </Title>
          <div style={chipsStyle}>
            <span style={chipStyle("#e5e7eb")}>{t("doctor_elig_summary_total", "Total")}: {summary.total}</span>
            <span style={chipStyle("#dbeafe", "#1677ff")}>{t("doctor_elig_summary_pending", "En attente")}: {summary.pending}</span>
            <span style={chipStyle("#dcfce7", "#166534")}>{t("doctor_elig_summary_accepted", "Acceptées")}: {summary.accepted}</span>
            <span style={chipStyle("#fee2e2", "#b91c1c")}>{t("doctor_elig_summary_rejected", "Refusées")}: {summary.rejected}</span>
          </div>
        </div>
        <Button type="primary" onClick={fetchList}>
          {t("doctor_elig_button_refresh", "Actualiser")}
        </Button>
      </div>

      <Table
        rowKey={(record) => String(record.dispatchId || record.requestId || record.id)}
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 8, showSizeChanger: false }}
      />

      <Modal
        open={reDispatchModal.open}
        title={t("doctor_elig_modal_resend_title", "Renvoyer la demande")}
        onCancel={closeReDispatchModal}
        onOk={confirmReDispatch}
        confirmLoading={reDispatchModal.loading}
        okText={t("doctor_elig_modal_resend_ok", "Renvoyer")}
      >
        <p>{t("doctor_elig_modal_resend_description", "Choisissez la nouvelle clinique destination.")}</p>
        <Select
          style={{ width: "100%" }}
          placeholder={t("doctor_elig_modal_resend_placeholder", "Sélectionner une clinique")}
          options={clinics}
          value={reDispatchModal.destinationClinicId}
          onChange={(value) =>
            setReDispatchModal((prev) => ({ ...prev, destinationClinicId: value }))
          }
          showSearch
          optionFilterProp="label"
        />
      </Modal>
    </div>
  );
}




